import type { EraDef } from '../types';

/**
 * ERA 10 — END OF TIME
 * The final era: an abstract void beyond the last second. Nothing here is where it
 * is — bodies hunt your afterimage from a moment ago, and the ones that reach you
 * get yanked back through a corridor they already walked, which then burns. Bullets
 * fly out, stall dead in the void, hang, and come back the way they came. Drifting
 * time-shards and broken, still-turning clock gears float in the black. Boss: The
 * Devourer of Hours, which stamps the exact spot you are standing in, arms a
 * swelling zone under your feet, then detonates it — the swallowed moment fires
 * back at wherever you've moved to and spits out afterimages of you. Palette:
 * void black with hourglass amber and hot magenta. Enemy bullets magenta
 * (0xff5cf5); hazards hourglass amber (0xffc46a) — a third hue distinct from both
 * bullets and the violet body/prop tint, fixing the placeholder stub's collision
 * between hazard and enemy-bullet colors.
 * Signature behaviors owned here: `lag`, `unwind`. Boss special: `devour`.
 */
const ENEMY_BULLET = 0xff5cf5;
const HAZARD = 0xffc46a;
const VOID_VIOLET = 0xbf5cff;

export const ERA_ENDOFTIME: EraDef = {
  index: 9,
  id: 'endoftime',
  name: 'End of Time',
  subtitle: 'Beyond the last second',
  implemented: true,
  palette: {
    sky: 0x000000,
    fog: 0x110a22,
    fogDensity: 0.027,
    ground: 0x070112,
    groundAlt: 0x1b0634,
    ambient: 0x56256f,
    sun: 0xff5cf5,
    sunIntensity: 1.5,
    accent: 0x8ff6ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'void',
    arenaRadius: 76,
    groundStyle: 'void',
    props: [
      { visual: { geo: 'octa', color: VOID_VIOLET, emissive: ENEMY_BULLET, emissiveIntensity: 1.2, scaleY: 1.6, anim: 'pulse' }, count: 20, scale: [0.3, 1.1], y: 2.2 },
      { visual: { geo: 'torus', color: 0x2a0a44, emissive: HAZARD, emissiveIntensity: 0.9, scaleY: 0.25, anim: 'spin' }, count: 12, scale: [0.9, 2.8], y: 0.6 },
      { visual: { geo: 'blob', color: 0x14001f, emissive: VOID_VIOLET, emissiveIntensity: 0.5, anim: 'wobble' }, count: 10, scale: [0.5, 1.3], y: 1.0 },
    ],
  },
  enemies: [
    {
      id: 't10_echo',
      name: 'Fading Echo',
      archetype: 'swarmer',
      hp: 42,
      speed: 6.2,
      damage: 12,
      radius: 0.36,
      xp: 11,
      visual: { geo: 'blob', color: 0x6a1a9a, emissive: ENEMY_BULLET, emissiveIntensity: 0.9, scale: 0.78, anim: 'wobble' },
      behavior: { type: 'lag', lagTime: 1.3, speedMult: 1.0, arriveRadius: 0.9 },
      // Signature swarm: 6.2 u/s but it only ever runs at the spot you occupied when the clock last ticked,
      // so it arrives late — and arrives exactly where you stopped to shoot. Counterplay: fire in short
      // bursts and drift between them; a standing kill-spot becomes the flock's rally point in 1.3 s.
    },
    {
      id: 't10_wraith',
      name: 'Chrono Wraith',
      archetype: 'shooter',
      hp: 64,
      speed: 3.4,
      damage: 12,
      radius: 0.48,
      xp: 16,
      shardChance: 0.1,
      visual: { geo: 'spike', color: 0x3a0a5a, emissive: ENEMY_BULLET, emissiveIntensity: 0.9, scale: 1.0, anim: 'spin' },
      behavior: { type: 'keepDistance', distance: 13, strafe: 0.35 },
      patterns: ['t10_stopped_clock'],
      patternCooldown: 2.6,
      fireRange: 18,
      // The era's teacher: three shells drift out ~8.5 u, stop dead and hang, then each un-fires itself as a
      // 16 u/s needle. Counterplay: the hang is the tell — step off the shell's line before it comes back,
      // and never retreat straight down the lane you were shot along.
    },
    {
      id: 't10_stillpoint',
      name: 'Stillpoint',
      archetype: 'shooter',
      hp: 84,
      speed: 1.6,
      damage: 12,
      radius: 0.60,
      xp: 18,
      shardChance: 0.12,
      visual: { geo: 'torus', color: 0x241038, emissive: HAZARD, emissiveIntensity: 0.9, scale: 1.1, scaleY: 0.5, anim: 'pulse' },
      behavior: { type: 'drift', jitter: 0.8 },
      patterns: ['t10_stall_field'],
      patternCooldown: 2.4,
      fireRange: 15,
      // Hazard author: it can never come to you — it just stalls time around itself, dropping two concentric
      // rings of amber pools (2.4 u and 6.7 u, angularly offset). Counterplay: kill it from outside 15 u, or
      // weave the offset slots between the rings; do not brawl inside the flower.
    },
    {
      id: 't10_unraveler',
      name: 'Unraveler',
      archetype: 'tank',
      hp: 170,
      speed: 3.4,
      damage: 22,
      radius: 0.92,
      xp: 32,
      shardChance: 0.16,
      visual: { geo: 'octa', color: 0x2a0a44, emissive: HAZARD, emissiveIntensity: 0.55, scale: 1.5, scaleY: 1.1, anim: 'spin' },
      behavior: { type: 'unwind', advanceTime: 2.6, speedMult: 1.15, snapTelegraph: 0.7,
                  scarCount: 4, scarRadius: 1.30, scarTelegraph: 0.45, scarDps: 13, scarDuration: 3.4, recoverTime: 1.8 },
      patterns: ['t10_hour_shards'],
      patternCooldown: 3.0,
      fireRange: 15,
      // Signature heavy: covers ~10 u toward you, freezes red for 0.7 s, then snaps back to where it started
      // and leaves four armed amber discs down the line it un-walked. Counterplay: the freeze is a free burst
      // window, but do not take it standing in its lane — the scar lands on the path, not on the boss's body.
    },
    {
      id: 't10_houreater',
      name: 'Hour Eater',
      archetype: 'tank',
      hp: 150,
      speed: 3.0,
      damage: 21,
      radius: 0.88,
      xp: 30,
      shardChance: 0.15,
      visual: { geo: 'blob', color: 0x1a0030, emissive: HAZARD, emissiveIntensity: 0.45, scale: 1.45, anim: 'bob' },
      behavior: { type: 'charge', windup: 0.7, speedMult: 2.6, duration: 1.0, cooldown: 2.5 },
      trail: { radius: 1.0, duration: 3.0, dps: 9, interval: 0.3, color: HAZARD },
      onDeath: { pattern: 't10_spent_burst' },
      // Filler charger with teeth: it eats the seconds under it and leaves the spent ones smoking, then vents
      // them when it dies. Counterplay: read the 0.7 s windup like any charger, fight it in open void because
      // its old runs stay lethal, and never finish it point-blank.
    },
    {
      id: 't10_herald',
      name: 'Herald of the Last Second',
      archetype: 'elite',
      elite: true,
      hp: 110,
      speed: 4.2,
      damage: 18,
      radius: 0.74,
      xp: 38,
      shardChance: 0.4,
      visual: { geo: 'torus', color: ENEMY_BULLET, emissive: ENEMY_BULLET, emissiveIntensity: 1.0, scale: 1.3, anim: 'spin' },
      behavior: { type: 'unwind', advanceTime: 1.8, speedMult: 1.30, snapTelegraph: 0.5,
                  scarCount: 5, scarRadius: 1.25, scarTelegraph: 0.35, scarDps: 15, scarDuration: 3.8, recoverTime: 1.1 },
      patterns: ['t10_rewind_spiral', 't10_hour_shards'],
      patternCooldown: 2.1,
      fireRange: 18,
      // Elite and the boss's rehearsal: it unwinds twice as often as an Unraveler and its spiral puts 16 stalled
      // shells in the air that all un-fire at once. Counterplay: it cannot shoot during its 0.5 s freeze and it
      // always returns to a point you can predict — hold the side of its lane and punish the freeze.
    },
    // Boss-only: born from `devour` when the swallowed moment detonates. Not a field spawn.
    {
      id: 't10_afterimage',
      name: 'Afterimage',
      archetype: 'swarmer',
      hp: 58,
      speed: 5.0,
      damage: 14,
      radius: 0.34,
      xp: 8,
      shardChance: 0.05,
      visual: { geo: 'blob', color: 0x8f4fd0, emissive: 0xffffff, emissiveIntensity: 0.7, scale: 0.70, anim: 'wobble' },
      behavior: { type: 'chase' },
      // Not a field spawn: these are YOU, a second ago, spat back out of the moment the Devourer swallowed.
      // Unlike the Fading Echoes they chase live and they are paper-thin. Counterplay: they spawn on the zone
      // you just vacated — turn and clear them while they are still bunched, before the next devour lands.
    },
  ],
  patterns: [
    {
      id: 't10_stopped_clock',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.55, bursts: 2, count: 3, spread: 0.45,
          bullet: { speed: 12, accel: -8.5, minSpeed: 0.4, lifetime: 1.75, radius: 0.28, damage: 13,
                    visual: 'ring', color: ENEMY_BULLET, onExpire: 't10_rewind_return' } },
      ],
    },
    {
      id: 't10_rewind_return',
      emitters: [
        { shape: 'aimed', interval: 1, bursts: 1, count: 1,
          bullet: { speed: 16, lifetime: 1.3, radius: 0.24, damage: 13, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_stall_field',
      emitters: [
        { shape: 'radial', start: 0, interval: 1, bursts: 1, count: 6, angle: 0,
          bullet: { speed: 6, accel: -7.5, minSpeed: 0.1, lifetime: 1.1, radius: 0.30, damage: 12, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.5, duration: 4.2, dps: 12, color: HAZARD } } },
        { shape: 'radial', start: 0.5, interval: 1, bursts: 1, count: 8, angle: 0.39,
          bullet: { speed: 10, accel: -7.5, minSpeed: 0.1, lifetime: 1.6, radius: 0.30, damage: 12, visual: 'ring', color: HAZARD,
                    linger: { radius: 1.5, duration: 4.2, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 't10_hour_shards',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.40, bursts: 2, count: 5, spread: 1.10,
          bullet: { speed: 9.5, curve: 0.55, lifetime: 2.0, radius: 0.26, damage: 14, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'aimed', start: 0.2, interval: 0.40, bursts: 2, count: 5, spread: 1.10,
          bullet: { speed: 9.5, curve: -0.55, lifetime: 2.0, radius: 0.26, damage: 14, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_rewind_spiral',
      emitters: [
        { shape: 'spiral', start: 0, interval: 0.30, bursts: 4, count: 4, rotatePerBurst: 0.42,
          bullet: { speed: 11, accel: -7, minSpeed: 0.3, lifetime: 1.95, radius: 0.26, damage: 13,
                    visual: 'ring', color: ENEMY_BULLET, onExpire: 't10_rewind_return' } },
      ],
    },
    {
      id: 't10_spent_burst',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 9,
          bullet: { speed: 3.8, accel: -2.4, minSpeed: 0.3, lifetime: 1.4, radius: 0.34, damage: 15, visual: 'blob', color: HAZARD,
                    linger: { radius: 1.4, duration: 3.2, dps: 12, color: HAZARD } } },
      ],
    },
    {
      id: 't10_boss_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 18,
          bullet: { speed: 6.5, lifetime: 2.0, radius: 0.32, damage: 18, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_boss_hourhand',
      emitters: [
        { shape: 'spiral', start: 0, interval: 0.22, bursts: 9, count: 2, rotatePerBurst: 0.52,
          bullet: { speed: 7.2, curve: 0.30, lifetime: 2.6, radius: 0.28, damage: 16, visual: 'orb', color: ENEMY_BULLET } },
        { shape: 'spiral', start: 0.11, interval: 0.22, bursts: 9, count: 2, rotatePerBurst: -0.52,
          bullet: { speed: 7.2, curve: -0.30, lifetime: 2.6, radius: 0.28, damage: 16, visual: 'orb', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_boss_rewind_volley',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 14,
          bullet: { speed: 14, accel: -9, minSpeed: 0.35, lifetime: 1.95, radius: 0.30, damage: 17,
                    visual: 'ring', color: ENEMY_BULLET, onExpire: 't10_boss_rewind_return' } },
      ],
    },
    {
      id: 't10_boss_rewind_return',
      emitters: [
        { shape: 'aimed', interval: 1, bursts: 1, count: 1,
          bullet: { speed: 17, lifetime: 1.4, radius: 0.24, damage: 16, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_boss_last_volley',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 18, angle: 0.17,
          bullet: { speed: 15, accel: -9, minSpeed: 0.35, lifetime: 2.05, radius: 0.30, damage: 17,
                    visual: 'ring', color: ENEMY_BULLET, onExpire: 't10_boss_rewind_return' } },
        { shape: 'radial', interval: 1, bursts: 1, count: 9,
          bullet: { speed: 5.0, lifetime: 2.6, radius: 0.36, damage: 16, visual: 'orb', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_boss_secondhand',
      emitters: [
        { shape: 'aimed', start: 0, interval: 0.28, bursts: 4, count: 3, spread: 0.85,
          bullet: { speed: 15, wave: { amp: 5, freq: 1.3 }, lifetime: 1.8, radius: 0.22, damage: 15, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_recall_echo',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 8,
          bullet: { speed: 6.8, lifetime: 1.8, radius: 0.30, damage: 16, visual: 'star', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_recall_burst',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 12,
          bullet: { speed: 7.4, lifetime: 1.9, radius: 0.30, damage: 17, visual: 'star', color: ENEMY_BULLET } },
        { shape: 'aimed', interval: 1, bursts: 1, count: 3, spread: 0.5,
          bullet: { speed: 13, lifetime: 1.6, radius: 0.24, damage: 16, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_recall_collapse',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 14, angle: 0.22,
          bullet: { speed: 7.6, curve: 0.20, lifetime: 2.0, radius: 0.30, damage: 17, visual: 'star', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 6,
          bullet: { speed: 9, accel: -8, minSpeed: 0.3, lifetime: 1.4, radius: 0.28, damage: 16,
                    visual: 'ring', color: ENEMY_BULLET, onExpire: 't10_boss_rewind_return' } },
      ],
    },
    {
      id: 't10_boss_phase2_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20,
          bullet: { speed: 7.2, lifetime: 2.0, radius: 0.30, damage: 18, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 12, angle: 0.26,
          bullet: { speed: 4.4, lifetime: 2.6, radius: 0.36, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 't10_boss_phase3_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 24,
          bullet: { speed: 7.4, curve: 0.20, lifetime: 2.1, radius: 0.30, damage: 18, visual: 'star', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 14, angle: 0.22,
          bullet: { speed: 4.2, lifetime: 2.8, radius: 0.40, damage: 16, visual: 'ring', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, bursts: 1, count: 10, angle: 0.11,
          bullet: { speed: 12, accel: -9, minSpeed: 0.35, lifetime: 1.9, radius: 0.28, damage: 17,
                    visual: 'ring', color: ENEMY_BULLET, onExpire: 't10_boss_rewind_return' } },
      ],
    },
  ],
  boss: {
    id: 't10_devourer',
    name: 'The Devourer of Hours',
    title: 'The Final Second',
    // Balance (final boss of the whole run — deliberately outside the usual 60-75s band): stage g=29, hp scale = (1+0.18*29)(1+0.15*9) = 14.617x -> ~61,391 effective HP. devour has no damage reduction, but evicts the player from their firing spot every 7-11s (~1.2s forced movement per cycle, ~12% throughput loss under the stationary-fire rule), against which the boss pays back its own rooted, guns-cold windup. Net target: ~80-95s — one full pattern family longer than eras 7-9's band, intentional as the campaign's capstone fight. Tune inside 3800-4600 base before touching devour params; raising echoCount past 3 in P3, or dropping P3 windup below 0.9s, makes the zone unreadable rather than hard.
    hp: 4200,
    radius: 2.8,
    speed: 2.1,
    damage: 42,
    xp: 720,
    visual: { geo: 'blob', color: 0x0a0212, emissive: ENEMY_BULLET, emissiveIntensity: 1.3, scale: 1.55, anim: 'pulse' },
    phases: [
      {
        hpBelow: 1,
        name: 'The Hour Hand Stops',
        behavior: { type: 'keepDistance', distance: 13, strafe: 0.15 },
        patterns: ['t10_boss_hourhand', 't10_boss_rewind_volley'],
        patternCooldown: 2.4,
        transition: 1.5,
        onEnter: 't10_boss_enter',
        special: { type: 'devour', interval: 11, windup: 1.6, radius: 3.6, dps: 14, duration: 2.6,
                   recallPattern: 't10_recall_echo', echoEnemy: 't10_afterimage', echoCount: 1, echoSpread: 2.0 },
      },
      {
        hpBelow: 0.66,
        name: 'Swallowing the Minute',
        behavior: { type: 'chase' },
        speedMult: 1.1,
        patterns: ['t10_boss_hourhand', 't10_boss_rewind_volley', 't10_boss_secondhand'],
        patternCooldown: 1.9,
        transition: 2,
        onEnter: 't10_boss_phase2_enter',
        special: { type: 'devour', interval: 9, windup: 1.25, radius: 4.2, dps: 18, duration: 3.0,
                   recallPattern: 't10_recall_burst', echoEnemy: 't10_afterimage', echoCount: 2, echoSpread: 2.4 },
      },
      {
        hpBelow: 0.33,
        name: 'Beyond the Last Second',
        behavior: { type: 'orbit', radius: 8, angular: 0.85 },
        speedMult: 1.2,
        patterns: ['t10_boss_secondhand', 't10_boss_last_volley', 't10_boss_hourhand'],
        patternCooldown: 1.5,
        transition: 2.2,
        onEnter: 't10_boss_phase3_enter',
        summons: { enemy: 't10_echo', count: 4, interval: 9 },
        special: { type: 'devour', interval: 7, windup: 1.0, radius: 4.8, dps: 22, duration: 3.4,
                   recallPattern: 't10_recall_collapse', echoEnemy: 't10_afterimage', echoCount: 3, echoSpread: 2.8 },
      },
    ],
  },
  stages: [
    {
      name: 'The Unwinding',
      duration: 240,
      objective: { type: 'hunt', enemy: 't10_unraveler', count: 8, label: 'Hunt the Unravelers' },
      density: { start: 1.4, end: 3.4 },
      pool: [
        { enemy: 't10_echo', weight: 10 },
        { enemy: 't10_wraith', weight: 4, minTime: 20 },
        { enemy: 't10_stillpoint', weight: 3, minTime: 45 },
        { enemy: 't10_unraveler', weight: 2, minTime: 75 },
        { enemy: 't10_houreater', weight: 2, minTime: 100 },
      ],
      waves: [
        { t: 5, enemy: 't10_echo', count: 6, formation: 'ring' },
        { t: 22, enemy: 't10_wraith', count: 3, formation: 'line' },
        { t: 48, enemy: 't10_echo', count: 20, formation: 'cluster' },
        { t: 70, enemy: 't10_stillpoint', count: 3, formation: 'random' },
        { t: 95, enemy: 't10_unraveler', count: 2, formation: 'edge' },
        { t: 125, enemy: 't10_wraith', count: 5, formation: 'random' },
        { t: 155, enemy: 't10_houreater', count: 2, formation: 'edge' },
        { t: 185, enemy: 't10_stillpoint', count: 4, formation: 'ring' },
        { t: 212, enemy: 't10_herald', count: 1, formation: 'edge' },
      ],
    },
    {
      name: 'Where the Clocks Stop',
      duration: 270,
      objective: { type: 'defend', seconds: 190, health: 150, radius: 8, label: 'Defend the final temporal anchor' },
      density: { start: 1.8, end: 4.0 },
      pool: [
        { enemy: 't10_echo', weight: 8 },
        { enemy: 't10_wraith', weight: 5 },
        { enemy: 't10_stillpoint', weight: 4 },
        { enemy: 't10_unraveler', weight: 3, minTime: 25 },
        { enemy: 't10_houreater', weight: 3, minTime: 45 },
        { enemy: 't10_herald', weight: 1, minTime: 90 },
      ],
      waves: [
        { t: 5, enemy: 't10_wraith', count: 4, formation: 'ring' },
        { t: 30, enemy: 't10_unraveler', count: 2, formation: 'edge' },
        { t: 58, enemy: 't10_stillpoint', count: 4, formation: 'ring' },
        { t: 85, enemy: 't10_echo', count: 26, formation: 'ring' },
        { t: 110, enemy: 't10_herald', count: 1, formation: 'edge' },
        { t: 140, enemy: 't10_houreater', count: 3, formation: 'line' },
        { t: 168, enemy: 't10_unraveler', count: 3, formation: 'edge' },
        { t: 198, enemy: 't10_wraith', count: 6, formation: 'cluster' },
        { t: 225, enemy: 't10_echo', count: 34, formation: 'ring' },
        { t: 250, enemy: 't10_herald', count: 2, formation: 'line' },
      ],
    },
    {
      name: 'The Last Second',
      duration: 600,
      density: { start: 1.0, end: 1.0 },
      pool: [
        { enemy: 't10_echo', weight: 6 },
        { enemy: 't10_wraith', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 't10_echo', count: 10, formation: 'ring' }],
      boss: { id: 't10_devourer', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_null_mantle', weight: 2 },
    { itemId: 'r_shattered_hourglass', weight: 2 },
    { itemId: 'w_chrono_cannon', weight: 2 },
    { itemId: 'w_flintlock', weight: 1 },
    { itemId: 'r_amber', weight: 2 },
    { itemId: 'a_hide', weight: 2 },
    { itemId: 'r_trilobite_eye', weight: 1 },
  ],
  audio: { music: 'music/era10_endoftime', ambient: 'amb/void', sfxSet: 'temporal' },
};
